// services/PlatformSettingsService.js - ReactJS (web)
import api from '../api/api';

class PlatformSettingsService {
  // ==================== PUBLIC SETTINGS ====================

  /**
   * Get public settings (frontend)
   * GET /api/platform-settings/public
   */
  async getPublicSettings() {
    try {
      const response = await api.get('/platform-settings/public');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation statistics (public)
   * GET /api/platform-settings/donation-stats
   */
  async getDonationStatistics() {
    try {
      const response = await api.get('/platform-settings/donation-stats');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get active donation goals summary (public)
   * GET /api/platform-settings/donation-goals/active/summary
   */
  async getActiveGoalsSummary() {
    try {
      const response = await api.get('/platform-settings/donation-goals/active/summary');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation goal progress (public)
   * GET /api/platform-settings/donation-goals/:index/progress
   */
  async getGoalProgress(index) {
    try {
      const response = await api.get(`/platform-settings/donation-goals/${index}/progress`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Validate donation amount (public)
   * POST /api/platform-settings/validate-donation
   */
  async validateDonationAmount(amount) {
    try {
      const response = await api.post('/platform-settings/validate-donation', { amount });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Process donation (public)
   * POST /api/platform-settings/process-donation
   */
  async processDonation(amount, goalIndex) {
    try {
      const response = await api.post('/platform-settings/process-donation', { amount, goalIndex });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== ADMIN SETTINGS ====================

  /**
   * Get all settings (admin only)
   * GET /api/platform-settings
   */
  async getAllSettings() {
    try {
      const response = await api.get('/platform-settings');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== DONATION SETTINGS ====================

  /**
   * Update donation settings with validation
   * PUT /api/platform-settings/donation
   */
  async updateDonationSettings(updateData) {
    try {
      const response = await api.put('/platform-settings/donation', updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== PLATFORM INFO ====================

  /**
   * Update platform info
   * PUT /api/platform-settings/platform-info
   */
  async updatePlatformInfo(updateData) {
    try {
      const response = await api.put('/platform-settings/platform-info', updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== DONATION GOALS ====================

  /**
   * Create new donation goal with notification
   * POST /api/platform-settings/donation-goals
   */
  async createDonationGoal(goalData) {
    try {
      const response = await api.post('/platform-settings/create-donation-goals', goalData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update donation goal with notification
   * PUT /api/platform-settings/donation-goals/:index
   */
  async updateDonationGoal(index, updateData) {
    try {
      const response = await api.put(`/platform-settings/donation-goals/${index}`, updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete donation goal with notification
   * DELETE /api/platform-settings/donation-goals/:index
   */
  async deleteDonationGoal(index) {
    try {
      const response = await api.delete(`/platform-settings/donation-goals/${index}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== FEATURES ====================

  /**
   * Update features with validation
   * PUT /api/platform-settings/features
   */
  async updateFeatures(updateData) {
    try {
      const response = await api.put('/platform-settings/features', updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Toggle feature
   * POST /api/platform-settings/toggle-feature/:featureName
   */
  async toggleFeature(featureName) {
    try {
      const response = await api.post(`/platform-settings/toggle-feature/${featureName}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== NOTIFICATION SETTINGS ====================

  /**
   * Update notification settings
   * PUT /api/platform-settings/notifications
   */
  async updateNotificationSettings(updateData) {
    try {
      const response = await api.put('/platform-settings/notifications', updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== STRIPE CONFIGURATION ====================

  /**
   * Update stripe configuration
   * PUT /api/platform-settings/stripe
   */
  async updateStripeConfig(updateData) {
    try {
      const response = await api.put('/platform-settings/stripe', updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== SETTINGS MANAGEMENT ====================

  /**
   * Reset all settings to default
   * POST /api/platform-settings/reset
   */
  async resetToDefaults() {
    try {
      const response = await api.post('/platform-settings/reset');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get settings audit log (all changes)
   * GET /api/platform-settings/audit
   */
  async getSettingsAuditLog() {
    try {
      const response = await api.get('/platform-settings/audit');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== DONATION MANAGEMENT (NEW) ====================

  /**
   * Get donation management settings
   * GET /api/platform-settings/donation-management
   */
  async getDonationManagement() {
    try {
      const response = await api.get('/platform-settings/donation-management');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get featured donations with full data
   * GET /api/platform-settings/donation-management/featured
   */
  async getFeaturedDonations() {
    try {
      const response = await api.get('/platform-settings/donation-management/featured');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get pending review donations
   * GET /api/platform-settings/donation-management/pending-review
   */
  async getPendingReviewDonations() {
    try {
      const response = await api.get('/platform-settings/donation-management/pending-review');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get flagged donations
   * GET /api/platform-settings/donation-management/flagged
   */
  async getFlaggedDonations() {
    try {
      const response = await api.get('/platform-settings/donation-management/flagged');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get highlighted donations
   * GET /api/platform-settings/donation-management/highlighted
   */
  async getHighlightedDonations() {
    try {
      const response = await api.get('/platform-settings/donation-management/highlighted');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== DONATION MANAGEMENT ACTIONS ====================

  /**
   * Feature a donation (Admin only)
   * POST /api/platform-settings/donation-management/:donationId/feature
   */
  async featureDonation(donationId) {
    try {
      const response = await api.post(`/platform-settings/donation-management/${donationId}/feature`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Unfeature a donation (Admin only)
   * DELETE /api/platform-settings/donation-management/:donationId/feature
   */
  async unfeatureDonation(donationId) {
    try {
      const response = await api.delete(`/platform-settings/donation-management/${donationId}/feature`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Hide a donation from public view (Admin only)
   * POST /api/platform-settings/donation-management/:donationId/hide
   */
  async hideDonation(donationId, reason = '') {
    try {
      const response = await api.post(`/platform-settings/donation-management/${donationId}/hide`, { reason });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Show a donation publicly (Admin only)
   * DELETE /api/platform-settings/donation-management/:donationId/hide
   */
  async showDonation(donationId) {
    try {
      const response = await api.delete(`/platform-settings/donation-management/${donationId}/hide`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Highlight a donation (Admin only)
   * POST /api/platform-settings/donation-management/:donationId/highlight
   */
  async highlightDonation(donationId) {
    try {
      const response = await api.post(`/platform-settings/donation-management/${donationId}/highlight`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Unhighlight a donation (Admin only)
   * DELETE /api/platform-settings/donation-management/:donationId/highlight
   */
  async unhighlightDonation(donationId) {
    try {
      const response = await api.delete(`/platform-settings/donation-management/${donationId}/highlight`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Flag a donation as fraudulent (Admin only)
   * POST /api/platform-settings/donation-management/:donationId/flag
   */
  async flagDonation(donationId, reason = '') {
    try {
      const response = await api.post(`/platform-settings/donation-management/${donationId}/flag`, { reason });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Unflag a donation (Admin only)
   * DELETE /api/platform-settings/donation-management/:donationId/flag
   */
  async unflagDonation(donationId) {
    try {
      const response = await api.delete(`/platform-settings/donation-management/${donationId}/flag`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Override donation status (Admin only)
   * PUT /api/platform-settings/donation-management/:donationId/status
   */
  async overrideDonationStatus(donationId, status, reason = '') {
    try {
      const response = await api.put(`/platform-settings/donation-management/${donationId}/status`, { status, reason });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    let errorMessage = ' ';
    
    if (error.response?.data?.data?.error?.message) {
      errorMessage = error.response.data.data.error.message;
    } else if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status,
      data: error.response?.data
    };
  }

  /**
   * Get default donation settings
   */
  getDefaultDonationSettings() {
    return {
      minDonationAmount: 1,
      maxDonationAmount: 10000,
      defaultCurrency: 'USD',
      allowedCurrencies: ['USD'],
      platformFeePercentage: 0,
      thankYouMessage: 'Thank you for your generous donation to the Haitian Services Platform!',
      displayDonations: true,
      displayDonorNames: true,
      showDonationProgress: true,
      allowAnonymousDonations: true
    };
  }

  /**
   * Get default platform info
   */
  getDefaultPlatformInfo() {
    return {
      name: 'Haitian Services Platform',
      description: '',
      mission: '',
      vision: '',
      contactEmail: '',
      contactPhone: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: ''
      }
    };
  }

  /**
   * Get default social links
   */
  getDefaultSocialLinks() {
    return {
      facebook: '',
      instagram: '',
      twitter: '',
      youtube: '',
      linkedin: ''
    };
  }

  /**
   * Get default features
   */
  getDefaultFeatures() {
    return {
      allowPublicDonations: true,
      showDonationProgress: true,
      allowAnonymousDonations: true
    };
  }

  /**
   * Get default donation management
   */
  getDefaultDonationManagement() {
    return {
      featuredDonations: [],
      hiddenDonations: [],
      highlightedDonations: [],
      pendingReviewDonations: [],
      flaggedDonations: [],
      statusOverrides: []
    };
  }

  /**
   * Get currency options
   */
  getCurrencyOptions() {
    return ['USD', 'EUR', 'GBP', 'CAD', 'HTG'];
  }

  /**
   * Format currency
   */
  formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  /**
   * Calculate donation progress
   */
  calculateProgress(current, target) {
    if (!target || target <= 0) return 0;
    return Math.min(((current / target) * 100), 100);
  }

  /**
   * Format donation goal for display
   */
  formatGoal(goal) {
    if (!goal) return null;
    
    return {
      ...goal,
      id: goal._id,
      progress: this.calculateProgress(goal.currentAmount, goal.targetAmount),
      remaining: Math.max(0, goal.targetAmount - goal.currentAmount),
      isComplete: goal.currentAmount >= goal.targetAmount,
      formattedCurrent: this.formatCurrency(goal.currentAmount),
      formattedTarget: this.formatCurrency(goal.targetAmount),
      formattedRemaining: this.formatCurrency(Math.max(0, goal.targetAmount - goal.currentAmount)),
      progressPercentage: this.calculateProgress(goal.currentAmount, goal.targetAmount).toFixed(1)
    };
  }

  /**
   * Format donation statistics
   */
  formatDonationStats(stats) {
    if (!stats) return null;
    
    return {
      ...stats,
      formattedTotalDonations: this.formatCurrency(stats.totalDonations),
      formattedTotalTarget: this.formatCurrency(stats.totalTarget),
      progress: parseFloat(stats.progress),
      progressDisplay: `${stats.progress}%`
    };
  }

  /**
   * Validate donation goal data
   */
  validateGoalData(goalData) {
    const errors = [];

    if (!goalData.title) {
      errors.push('Goal title is required');
    } else if (goalData.title.length < 3) {
      errors.push('Goal title must be at least 3 characters');
    }

    if (!goalData.targetAmount || goalData.targetAmount <= 0) {
      errors.push('Target amount must be greater than 0');
    } else if (goalData.targetAmount > 1000000) {
      errors.push('Target amount cannot exceed $1,000,000');
    }

    if (goalData.description && goalData.description.length > 500) {
      errors.push('Description cannot exceed 500 characters');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate donation amount
   */
  validateAmount(amount, min, max) {
    const errors = [];

    if (!amount || amount <= 0) {
      errors.push('Donation amount must be greater than 0');
    }

    if (min !== undefined && amount < min) {
      errors.push(`Minimum donation amount is $${min}`);
    }

    if (max !== undefined && amount > max) {
      errors.push(`Maximum donation amount is $${max}`);
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get feature labels
   */
  getFeatureLabels() {
    return {
      allowPublicDonations: 'Allow Public Donations',
      showDonationProgress: 'Show Donation Progress',
      allowAnonymousDonations: 'Allow Anonymous Donations'
    };
  }

  /**
   * Get feature descriptions
   */
  getFeatureDescriptions() {
    return {
      allowPublicDonations: 'Enable or disable public donations on the platform',
      showDonationProgress: 'Display donation progress bars on the platform',
      allowAnonymousDonations: 'Allow users to donate anonymously'
    };
  }

  /**
   * Get donation goal status
   */
  getGoalStatus(goal) {
    if (!goal) return 'unknown';
    if (goal.isComplete) return 'completed';
    if (!goal.isActive) return 'inactive';
    if (goal.currentAmount > 0) return 'in_progress';
    return 'pending';
  }

  /**
   * Get donation goal status label
   */
  getGoalStatusLabel(status) {
    const labels = {
      completed: '✅ Completed',
      inactive: '⏸️ Inactive',
      in_progress: '🔄 In Progress',
      pending: '⏳ Pending',
      unknown: '❓ Unknown'
    };
    return labels[status] || status;
  }

  /**
   * Get donation goal status color
   */
  getGoalStatusColor(status) {
    const colors = {
      completed: '#2ECC71',
      inactive: '#95A5A6',
      in_progress: '#3498DB',
      pending: '#F1C40F',
      unknown: '#95A5A6'
    };
    return colors[status] || '#95A5A6';
  }


// ==================== TERMS OF SERVICE & PRIVACY POLICY ====================

  /**
   * Get Terms of Service (public)
   * GET /api/platform-settings/terms-of-service
   */
  async getTermsOfService(language = 'en') {
    try {
      const response = await api.get('/platform-settings/terms-of-service', {
        params: { language }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get Privacy Policy (public)
   * GET /api/platform-settings/privacy-policy
   */
  async getPrivacyPolicy(language = 'en') {
    try {
      const response = await api.get('/platform-settings/privacy-policy', {
        params: { language }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get both Terms and Privacy (public)
   * GET /api/platform-settings/terms-and-privacy
   */
  async getTermsAndPrivacy(language = 'en') {
    try {
      const response = await api.get('/platform-settings/terms-and-privacy', {
        params: { language }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Record user acceptance of terms and privacy
   * POST /api/platform-settings/accept-terms
   */
  async acceptTermsAndPrivacy(userId, termsVersion, privacyVersion) {
    try {
      const response = await api.post('/platform-settings/accept-terms', {
        userId,
        termsVersion,
        privacyVersion
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check if user has accepted latest versions
   * GET /api/platform-settings/check-acceptance/:userId
   */
  async checkUserAcceptance(userId) {
    try {
      const response = await api.get(`/platform-settings/check-acceptance/${userId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== TERMS & PRIVACY (ADMIN) ====================

  /**
   * Update Terms of Service (Admin only)
   * PUT /api/platform-settings/terms-of-service
   */
  async updateTermsOfService(updateData) {
    try {
      const response = await api.put('/platform-settings/terms-of-service', updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update Privacy Policy (Admin only)
   * PUT /api/platform-settings/privacy-policy
   */
  async updatePrivacyPolicy(updateData) {
    try {
      const response = await api.put('/platform-settings/privacy-policy', updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get supported languages
   */
  getSupportedLanguages() {
    return [
      { code: 'en', name: 'English', flag: '🇺🇸' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' },
      { code: 'ht', name: 'Kreyòl', flag: '🇭🇹' }
    ];
  }

  /**
   * Get default language
   */
  getDefaultLanguage() {
    return 'en';
  }

  /**
   * Get language name by code
   */
  getLanguageName(code) {
    const languages = {
      en: 'English',
      fr: 'Français',
      ht: 'Kreyòl'
    };
    return languages[code] || code;
  }

  /**
   * Get language flag by code
   */
  getLanguageFlag(code) {
    const flags = {
      en: '🇺🇸',
      fr: '🇫🇷',
      ht: '🇭🇹'
    };
    return flags[code] || '🌐';
  }

  /**
   * Validate language code
   */
  isValidLanguage(code) {
    return ['en', 'fr', 'ht'].includes(code);
  }

  /**
   * Get default terms of service (fallback)
   */
  getDefaultTermsOfService(language = 'en') {
    const defaultSections = [
      {
        title: '1. Acceptance of Terms',
        content: 'By using the Platform, you agree to comply with these Terms of Service. If you do not agree, please do not use the Platform.',
        order: 1
      },
      {
        title: '2. Description of Service',
        content: 'The Platform provides a marketplace connecting service providers with clients seeking various services.',
        order: 2
      },
      {
        title: '3. User Accounts',
        content: 'Providers must create an account to list services. Clients may use the platform as guests.',
        order: 3
      },
      {
        title: '4. User Conduct',
        content: 'Users agree to provide accurate information and comply with all applicable laws.',
        order: 4
      },
      {
        title: '5. Payments and Donations',
        content: 'The Platform is free to use. Donations are optional and support community projects.',
        order: 5
      }
    ];

    const translations = {
      fr: {
        title: "Conditions d'Utilisation",
        sections: defaultSections.map(s => ({
          ...s,
          title: `[FR] ${s.title}`,
          content: `[FR] ${s.content}`
        }))
      },
      ht: {
        title: "Kondisyon Sèvis",
        sections: defaultSections.map(s => ({
          ...s,
          title: `[HT] ${s.title}`,
          content: `[HT] ${s.content}`
        }))
      }
    };

    let title = 'Terms of Service';
    let sections = defaultSections;

    if (language !== 'en' && translations[language]) {
      title = translations[language].title;
      sections = translations[language].sections;
    }

    return {
      title,
      version: '1.0.0',
      lastUpdated: new Date().toISOString(),
      content: { sections },
      requiresAcceptance: true,
      acceptanceRequiredFor: 'all',
      isActive: true,
      language
    };
  }

  /**
   * Get default privacy policy (fallback)
   */
  getDefaultPrivacyPolicy(language = 'en') {
    const defaultSections = [
      {
        title: '1. Information We Collect',
        content: 'We collect information you provide directly, including name, contact information, and usage data.',
        order: 1
      },
      {
        title: '2. How We Use Information',
        content: 'We use collected information to connect clients with service providers and improve your experience.',
        order: 2
      },
      {
        title: '3. Information Sharing',
        content: 'We do not sell or rent your personal information.',
        order: 3
      },
      {
        title: '4. Data Security',
        content: 'We implement reasonable security measures to protect your information.',
        order: 4
      },
      {
        title: '5. User Rights',
        content: 'You have the right to access, correct, and delete your personal information.',
        order: 5
      }
    ];

    const translations = {
      fr: {
        title: "Politique de Confidentialité",
        sections: defaultSections.map(s => ({
          ...s,
          title: `[FR] ${s.title}`,
          content: `[FR] ${s.content}`
        }))
      },
      ht: {
        title: "Politik Konfidansyalite",
        sections: defaultSections.map(s => ({
          ...s,
          title: `[HT] ${s.title}`,
          content: `[HT] ${s.content}`
        }))
      }
    };

    let title = 'Privacy Policy';
    let sections = defaultSections;

    if (language !== 'en' && translations[language]) {
      title = translations[language].title;
      sections = translations[language].sections;
    }

    return {
      title,
      version: '1.0.0',
      lastUpdated: new Date().toISOString(),
      content: { sections },
      compliance: {
        gdprCompliant: true,
        dataRetentionPeriod: 365,
        cookieConsentRequired: true
      },
      dataCollection: {
        collectLocation: true,
        collectAnalytics: true,
        collectCookies: true
      },
      userRights: {
        rightToAccess: true,
        rightToRectification: true,
        rightToErasure: true,
        rightToObject: true,
        rightToDataPortability: true
      },
      isActive: true,
      language
    };
  }


}

export default new PlatformSettingsService();